import { useEffect, useState } from 'react';
import VideoPlayer from './VideoPlayer';
import { useViewContext } from '../../../../lib/context/ViewContext';
import { VideoDetailsProps } from './VideoDetails.types';
import LayoutContainer from '../../../common/Layout/Layout';
import { VideoChaptersSkeleton, VideoPlayerSkeleton } from './VideoSkeletons';
import { PageType } from '../../../../lib/hooks/usePageDetails';
import { DATA_ATTRIBUTES } from '../../../../lib/constants/attributes';
import { useConfig } from '../../../../lib/hooks/config';

const VideoDetails = ({
  chaptersHeading,
  lastUpdatePlaceholder,
  backButtonShow,
  backButtonLabel,
}: VideoDetailsProps) => {
  const [isScriptLoading, setIsScriptLoading] = useState(true);
  const { brightcoveAccountId, brightcovePlayerId } = useConfig() ?? {};
  const { page, isLoading } = useViewContext();
  const { video, type } = page;

  useEffect(() => {
    if (type === PageType.MicrolearningDetails || type === PageType.VideoDetails) {
      if (!isLoading && video && brightcoveAccountId && brightcovePlayerId) {
        const scriptSrc = `https://players.brightcove.net/${brightcoveAccountId}/${brightcovePlayerId}_default/index.min.js`;
        const scriptEl = document.head.querySelector<HTMLScriptElement>(`script[src="${scriptSrc}"]`);
        const handleScriptLoaded = (script: HTMLScriptElement) => {
          setIsScriptLoading(false);
          script?.setAttribute(DATA_ATTRIBUTES.STATUS, 'loaded');
        };

        if (!scriptEl) {
          setIsScriptLoading(true);
          const scriptTag = document.createElement('script');
          scriptTag.src = scriptSrc;
          scriptTag.async = true;
          scriptTag.addEventListener('load', () => handleScriptLoaded(scriptTag), { once: true });
          document.head.appendChild(scriptTag);
        } else if (scriptEl.getAttribute(DATA_ATTRIBUTES.STATUS) === 'loaded') {
          setIsScriptLoading(false);
        } else {
          scriptEl.addEventListener('load', () => handleScriptLoaded(scriptEl), { once: true });
        }
      }
    }
  }, [isLoading]);

  if ((type !== PageType.MicrolearningDetails && type !== PageType.VideoDetails) || (video && !video.id)) {
    return null;
  }

  return isLoading || !video || isScriptLoading || !window.videojs ? (
    <LayoutContainer>
      <VideoPlayerSkeleton />
      <VideoChaptersSkeleton />
    </LayoutContainer>
  ) : (
    <VideoPlayer
      key={video.id}
      chaptersHeading={chaptersHeading}
      lastUpdatePlaceholder={lastUpdatePlaceholder}
      backButtonShow={backButtonShow}
      backButtonLabel={backButtonLabel}
    />
  );
};

export default VideoDetails;
